import { useEffect, useRef, useState } from 'react';
import { drainOutbox } from 'services/outbox';
import { useClient } from 'urql';
import { MutationOutcome, getMutationOutcome, summariseOutcomes } from 'utils/offlineQueryMutation';
import useOfflineMutationResult from './useOfflineMutationResult';
import useOnlineStatus, { OnlineStatus } from './useOnlineStatus';

const INITIAL_RETRY_DELAY_MS = 5_000;
const MAX_RETRY_DELAY_MS = 5 * 60_000;

const useMutationQueueProcessor = () => {
  const isOnline = useOnlineStatus() !== OnlineStatus.Offline;
  const client = useClient();
  const { showMutationOutcome } = useOfflineMutationResult();
  const [retryTick, setRetryTick] = useState(0);
  const retryDelayRef = useRef(INITIAL_RETRY_DELAY_MS);

  // Read through a ref: the handler is new every render and must not start another drain.
  const showMutationOutcomeRef = useRef(showMutationOutcome);
  useEffect(() => {
    showMutationOutcomeRef.current = showMutationOutcome;
  });

  useEffect(() => {
    if (!isOnline) return;
    let isActive = true;
    let retryTimer: number | undefined;
    const outcomes: MutationOutcome[] = [];

    drainOutbox(
      ({ query, variables, additionalTypenames }) =>
        client.mutation(query, variables, { additionalTypenames }).toPromise(),
      (entry, result) => outcomes.push(getMutationOutcome(result, entry.messages)),
    ).then((isDrained) => {
      // Reported even after cleanup: these changes were sent and the user should hear how they went.
      if (outcomes.length) showMutationOutcomeRef.current(summariseOutcomes(outcomes));
      if (!isActive) return;
      if (isDrained) {
        retryDelayRef.current = INITIAL_RETRY_DELAY_MS;
        return;
      }
      // navigator.onLine can stay true on a network with no internet, so kept entries retry on a timer.
      retryTimer = window.setTimeout(() => setRetryTick((tick) => tick + 1), retryDelayRef.current);
      retryDelayRef.current = Math.min(retryDelayRef.current * 2, MAX_RETRY_DELAY_MS);
    });

    return () => {
      isActive = false;
      window.clearTimeout(retryTimer);
    };
  }, [isOnline, client, retryTick]);
};

export default useMutationQueueProcessor;
